import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
    IoLockClosedOutline,
    IoTimeOutline,
    IoMapOutline,
    IoLanguageOutline,
} from 'react-icons/io5';

const HowItWorks: React.FC = () => {
    const { t } = useTranslation();

    const features = [
        {
            icon: <IoLockClosedOutline className="w-8 h-8" />,
            title: t('how.noLogin.title'),
            description: t('how.noLogin.desc'),
            color: "from-primary-green to-emerald-600",
            shadow: "shadow-primary-green/20",
            step: "01"
        },
        {
            icon: <IoTimeOutline className="w-8 h-8" />,
            title: t('how.hours.title'),
            description: t('how.hours.desc'),
            color: "from-secondary-blue to-blue-600",
            shadow: "shadow-secondary-blue/20",
            step: "02"
        },
        {
            icon: <IoMapOutline className="w-8 h-8" />,
            title: t('how.maps.title'),
            description: t('how.maps.desc'),
            color: "from-ocean-blue to-cyan-600",
            shadow: "shadow-ocean-blue/20",
            step: "03"
        },
        {
            icon: <IoLanguageOutline className="w-8 h-8" />,
            title: t('how.languages.title'),
            description: t('how.languages.desc'),
            color: "from-navy to-indigo-900",
            shadow: "shadow-navy/20",
            step: "04"
        },
    ];

    return (
        <section id="how-it-works" className="py-24 lg:py-32 bg-gray-50 relative overflow-hidden">
            {/* Dynamic Background */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary-green/5 rounded-full blur-[100px] animate-pulse" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-secondary-blue/5 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-primary-green font-bold tracking-wider uppercase text-sm mb-4 block">
                            Simple & Accessible
                        </span>
                        <h2 className="text-4xl lg:text-6xl font-bold text-navy mb-6 tracking-tight">
                            {t('how.title')}
                        </h2>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            {t('how.subtitle')}
                        </p>
                    </motion.div>
                </div>

                <div className="relative">
                    {/* Connecting Line (Desktop) */}
                    <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-gray-200 to-transparent -translate-x-1/2" />

                    <div className="space-y-12 lg:space-y-32">
                        {features.map((feature, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-100px" }}
                                transition={{ duration: 0.7, delay: idx * 0.1 }}
                                className={`relative flex flex-col lg:flex-row items-center gap-8 lg:gap-20 ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                                    }`}
                            >
                                {/* Content Side */}
                                <div className={`flex-1 text-center lg:text-left ${idx % 2 === 1 ? 'lg:text-right' : ''}`}>
                                    <div className={`relative inline-block mb-6`}>
                                        <span className="absolute -top-8 -left-8 text-8xl font-bold text-gray-100 select-none -z-10">
                                            {feature.step}
                                        </span>
                                        <h3 className="text-3xl font-bold text-navy mb-4 relative z-10">
                                            {feature.title}
                                        </h3>
                                    </div>
                                    <p className="text-lg text-gray-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
                                        {feature.description}
                                    </p>
                                </div>

                                {/* Center Icon/Marker */}
                                <div className="relative z-10 flex-shrink-0">
                                    <div className="relative group">
                                        {/* Glow Effect */}
                                        <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-3xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500`} />

                                        {/* Icon Container */}
                                        <div className={`relative w-24 h-24 bg-white rounded-3xl shadow-xl flex items-center justify-center transform transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3`}>
                                            <div className={`text-primary-green`}>
                                                {React.cloneElement(feature.icon as any, { className: `w-10 h-10 ${idx === 0 ? 'text-primary-green' : idx === 1 ? 'text-secondary-blue' : idx === 2 ? 'text-ocean-blue' : 'text-navy'}` })}
                                            </div>                                        </div>
                                    </div>
                                </div>

                                {/* Empty Side for Balance */}
                                <div className="flex-1 hidden lg:block" />
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowItWorks;
